import React, { useEffect, useState } from 'react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Building2, Users, Clock, ChevronRight, MoreHorizontal, Plus, X, Home as HomeIcon, MapPin, Search, Filter } from 'lucide-react';
import { builderCommunities, regions } from '../data/optionsData';
export function CommunitiesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    builderId
  } = useParams<{
    builderId: string;
  }>();
  const isAdmin = location.pathname.startsWith('/admin');
  const basePath = isAdmin ? '/admin/communities' : `/${builderId}/communities`;
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newCommunity, setNewCommunity] = useState({
    name: '',
    description: '',
    regionId: ''
  });
  // Admin Table Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [regionFilter, setRegionFilter] = useState('all');
  const filteredCommunities = builderCommunities.filter(community => {
    const matchesSearch = community.name.toLowerCase().includes(searchQuery.toLowerCase()) || community.builderName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || community.status === statusFilter;
    const matchesRegion = regionFilter === 'all' || community.regionId === regionFilter;
    return matchesSearch && matchesStatus && matchesRegion;
  });
  // ESC key handler
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showCreateModal) {
        setShowCreateModal(false);
        setNewCommunity({
          name: '',
          description: '',
          regionId: ''
        });
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showCreateModal]);
  const handleCreateCommunity = () => {
    console.log('Creating community:', newCommunity);
    // In production, this would create the community
    setShowCreateModal(false);
    setNewCommunity({
      name: '',
      description: '',
      regionId: ''
    });
  };
  return <>
      <div className="space-y-6 max-w-7xl mx-auto" data-id="element-3659">
        <div className="flex items-center justify-between" data-id="element-3660">
          <div data-id="element-3661">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-3662">Communities</h1>
            <p className="text-slate-500" data-id="element-3663">
              Manage your active communities and their models.
            </p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} leftIcon={<Plus className="h-4 w-4" data-id="element-3665" />} data-id="element-3664">
            New Community
          </Button>
        </div>

        {isAdmin ? <div className="space-y-4" data-id="element-3666">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-3667">
              <div className="relative flex-1" data-id="element-3668">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-3669" />
                <input type="text" placeholder="Search communities or builders..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-shadow" data-id="element-3670" />
              </div>
              <div className="flex gap-4" data-id="element-3671">
                <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-sm font-medium text-slate-700" data-id="element-3672">
                  <option value="all" data-id="element-3673">All Statuses</option>
                  <option value="active" data-id="element-3674">Active</option>
                  <option value="draft" data-id="element-3675">Draft</option>
                  <option value="archived" data-id="element-3676">Archived</option>
                </select>
                <select value={regionFilter} onChange={e => setRegionFilter(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white text-sm font-medium text-slate-700" data-id="element-3677">
                  <option value="all" data-id="element-3678">All Regions</option>
                  {regions.map(r => <option key={r.id} value={r.id} data-id="element-3679">
                      {r.name}
                    </option>)}
                </select>
              </div>
            </div>

            {/* Table */}
            <Card className="overflow-hidden" data-id="element-3680">
              <div className="overflow-x-auto" data-id="element-3681">
                <table className="w-full text-sm text-left" data-id="element-3682">
                  <thead className="bg-slate-50 border-b border-slate-200 text-slate-500" data-id="element-3683">
                    <tr data-id="element-3684">
                      <th className="px-6 py-3 font-medium" data-id="element-3685">Community</th>
                      <th className="px-6 py-3 font-medium" data-id="element-3686">Builder</th>
                      <th className="px-6 py-3 font-medium" data-id="element-3687">Region</th>
                      <th className="px-6 py-3 font-medium" data-id="element-3688">Status</th>
                      <th className="px-6 py-3 font-medium" data-id="element-3689">Models</th>
                      <th className="px-6 py-3 font-medium" data-id="element-3690">Buyers</th>
                      <th className="px-6 py-3 font-medium" data-id="element-3691">Last Activity</th>
                      <th className="px-6 py-3 font-medium text-right" data-id="element-3692">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200" data-id="element-3693">
                    {filteredCommunities.map(community => <tr key={community.id} onClick={() => navigate(`${basePath}/${community.id}`)} className="hover:bg-slate-50 cursor-pointer transition-colors group" data-id="element-3694">
                        <td className="px-6 py-4" data-id="element-3695">
                          <div className="flex items-center gap-3" data-id="element-3696">
                            <div className="h-10 w-10 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 border border-slate-200" data-id="element-3697">
                              <img src={community.image} alt={community.name} className="h-full w-full object-cover" data-id="element-3698" />
                            </div>
                            <div className="font-medium text-slate-900 group-hover:text-primary-600 transition-colors" data-id="element-3699">
                              {community.name}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-slate-600" data-id="element-3700">
                          {community.builderName}
                        </td>
                        <td className="px-6 py-4 text-slate-600" data-id="element-3701">
                          <div className="flex items-center gap-1.5" data-id="element-3702">
                            <MapPin className="h-3.5 w-3.5 text-slate-400" data-id="element-3703" />
                            {community.regionName}
                          </div>
                        </td>
                        <td className="px-6 py-4" data-id="element-3704">
                          <Badge variant={community.status === 'active' ? 'success' : 'neutral'} className="capitalize" data-id="element-3705">
                            {community.status}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-slate-600" data-id="element-3706">
                          {community.modelCount}
                        </td>
                        <td className="px-6 py-4 text-slate-600" data-id="element-3707">
                          {community.buyerCount}
                        </td>
                        <td className="px-6 py-4 text-slate-500" data-id="element-3708">
                          {community.lastActivity}
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-3709">
                          <Button variant="ghost" size="sm" onClick={e => {
                      e.stopPropagation();
                      navigate(`${basePath}/${community.id}`);
                    }} data-id="element-3710">
                            View
                          </Button>
                        </td>
                      </tr>)}
                    {filteredCommunities.length === 0 && <tr data-id="element-3711">
                        <td colSpan={8} className="px-6 py-12 text-center text-slate-500" data-id="element-3712">
                          No communities found matching your filters.
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          </div> : <div className="grid grid-cols-1 gap-6" data-id="element-3713">
            {builderCommunities.map(community => <Card key={community.id} className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`${basePath}/${community.id}`)} data-id="element-3714">
                <div className="flex flex-col md:flex-row" data-id="element-3715">
                  {/* Image Section */}
                  <div className="w-full md:w-64 h-48 md:h-auto bg-slate-100 relative overflow-hidden flex-shrink-0" data-id="element-3716">
                    <img src={community.image} alt={community.name} className="w-full h-full object-cover" data-id="element-3717" />
                    <div className="absolute top-3 left-3 flex gap-2" data-id="element-3718">
                      <Badge variant={community.status === 'active' ? 'success' : 'neutral'} className="capitalize" data-id="element-3719">
                        {community.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="flex-1 p-6 flex flex-col justify-between" data-id="element-3720">
                    <div data-id="element-3721">
                      <div className="flex items-start justify-between mb-2" data-id="element-3722">
                        <div data-id="element-3723">
                          <div className="flex items-center gap-2 mb-1" data-id="element-3724">
                            <Badge variant="neutral" className="bg-slate-100 text-slate-600 border-slate-200" data-id="element-3725">
                              <MapPin className="h-3 w-3 mr-1" data-id="element-3726" />
                              {community.regionName}
                            </Badge>
                          </div>
                          <h3 className="text-xl font-bold text-slate-900 group-hover:text-primary-600 transition-colors" data-id="element-3727">
                            {community.name}
                          </h3>
                        </div>
                        <button className="text-slate-400 hover:text-slate-600 p-1" onClick={e => {
                    e.stopPropagation();
                    // Menu action
                  }} data-id="element-3728">
                          <MoreHorizontal className="h-5 w-5" data-id="element-3729" />
                        </button>
                      </div>
                      <p className="text-slate-500 mb-6 max-w-2xl line-clamp-2" data-id="element-3730">
                        {community.description}
                      </p>

                      <div className="flex items-center gap-6 text-sm text-slate-600" data-id="element-3731">
                        <div className="flex items-center gap-2" data-id="element-3732">
                          <HomeIcon className="h-4 w-4 text-slate-400" data-id="element-3733" />
                          <span className="font-medium" data-id="element-3734">
                            {community.modelCount} Models
                          </span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-3735">
                          <Users className="h-4 w-4 text-slate-400" data-id="element-3736" />
                          <span data-id="element-3737">{community.buyerCount} Active Buyers</span>
                        </div>
                        <div className="flex items-center gap-2" data-id="element-3738">
                          <Clock className="h-4 w-4 text-slate-400" data-id="element-3739" />
                          <span data-id="element-3740">Updated {community.lastActivity}</span>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6 pt-6 border-t border-slate-100 flex items-center gap-4" data-id="element-3741">
                      <Button onClick={e => {
                  e.stopPropagation();
                  navigate(`${basePath}/${community.id}`);
                }} leftIcon={<Building2 className="h-4 w-4" data-id="element-3743" />} data-id="element-3742">
                        View Community
                      </Button>
                      <Button variant="outline" onClick={e => {
                  e.stopPropagation();
                  navigate(`${basePath}/${community.id}/analytics`);
                }} data-id="element-3744">
                        View Analytics
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>)}
          </div>}
      </div>

      {/* Create Community Modal */}
      {showCreateModal && <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-id="element-3745">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200" data-id="element-3746">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-3747">
              <div data-id="element-3748">
                <h2 className="text-xl font-bold text-slate-900" data-id="element-3749">
                  Create New Community
                </h2>
                <p className="text-sm text-slate-500 mt-1" data-id="element-3750">
                  Add a new development community to your portfolio
                </p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-slate-600 transition-colors p-2 hover:bg-slate-100 rounded-lg" data-id="element-3751">
                <X className="h-5 w-5" data-id="element-3752" />
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-3753">
              <div data-id="element-3754">
                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3755">
                  Community Name <span className="text-red-500" data-id="element-3756">*</span>
                </label>
                <input type="text" value={newCommunity.name} onChange={e => setNewCommunity({
              ...newCommunity,
              name: e.target.value
            })} placeholder="e.g., The Riverside Collection" className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-shadow" autoFocus data-id="element-3757" />
              </div>

              <div data-id="element-3758">
                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3759">
                  Region <span className="text-red-500" data-id="element-3760">*</span>
                </label>
                <select value={newCommunity.regionId} onChange={e => setNewCommunity({
              ...newCommunity,
              regionId: e.target.value
            })} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-shadow bg-white" data-id="element-3761">
                  <option value="" disabled data-id="element-3762">
                    Select a region
                  </option>
                  {regions.map(region => <option key={region.id} value={region.id} data-id="element-3763">
                      {region.name}
                    </option>)}
                </select>
              </div>

              <div data-id="element-3764">
                <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-3765">
                  Description{' '}
                  <span className="text-slate-400 font-normal" data-id="element-3766">(Optional)</span>
                </label>
                <textarea value={newCommunity.description} onChange={e => setNewCommunity({
              ...newCommunity,
              description: e.target.value
            })} placeholder="Brief description of the community..." rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-shadow resize-none" data-id="element-3767" />
              </div>
            </div>
            <div className="p-6 border-t border-slate-100 flex justify-end gap-3 bg-slate-50" data-id="element-3768">
              <Button variant="outline" onClick={() => setShowCreateModal(false)} data-id="element-3769">
                Cancel
              </Button>
              <Button onClick={handleCreateCommunity} disabled={!newCommunity.name.trim() || !newCommunity.regionId} leftIcon={<Plus className="h-4 w-4" data-id="element-3771" />} data-id="element-3770">
                Create Community
              </Button>
            </div>
          </div>
        </div>}
    </>;
}